import Head from 'next/head';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import useAuth from '../components/hooks/useNewAuth';
import { BoltIcon, HandRaisedIcon } from '@heroicons/react/24/outline';
import cookieService from '../lib/cookie';
import LazyLoad from 'react-lazy-load';

export default function Index() {
  const { data, loading, error } = useAuth();
  const [hasLoggedIn, setHasLoggedIn] = useState(false);

  React.useEffect(() => {
    if (document) {
      const authedCookie = cookieService.getCookie('authed');
      setHasLoggedIn(authedCookie && authedCookie === 'true');
    }
  }, []);

  return (
    <div className="max-h-[100vh]">
      <Head>
        <title>StartScreen | Early access</title>
        <meta property="og:title" content="StartScreen" />
        <meta
          property="og:description"
          content="The next-generation employee training platform."
        />
        <script async src="https://tally.so/widgets/embed.js"></script>
      </Head>
      <div className="aliased bg-gradient-to-b from-[#12132d] via-[#000212] to-[#000212] text-white min-h-screen">
        <div
          className="mx-auto max-w-7xl px-5 py-8 sm:py-10 flex items-center justify-between text-base sm:text-lg font-medium"
          data-aos="fade-in"
        >
          <Link href="/">
            <img
              className="h-6 sm:h-7 w-auto cursor-pointer"
              src="/full-logo-mixed.png"
              alt="StartScreen Logo"
            />
          </Link>{' '}
          <div className="flex items-center">
            <a
              className="hidden sm:block py-2 px-6 rounded-md active:scale-95 hover:bg-[#272A3F] transition-all duration-200 ease-in-out "
              href="mailto:team@startscreen.io"
            >
              Contact us
            </a>
            {hasLoggedIn ? (
              <a
                target="_blank"
                className={
                  'hidden lg:block py-2 px-6 rounded-md active:scale-95 hover:bg-[#272A3F] transition-all duration-200 ease-in-out ' +
                  (hasLoggedIn && ' md:hidden')
                }
                href="https://join.slack.com/t/startscreenhq/shared_invite/zt-1hnr6oh2u-MLxK_xWFvruaQ806yocLaQ"
              >
                Community
              </a>
            ) : (
              <a
                target="_blank"
                className={
                  'hidden lg:block py-2 px-6 rounded-md active:scale-95 hover:bg-[#272A3F] transition-all duration-200 ease-in-out ' +
                  (hasLoggedIn && ' md:hidden')
                }
                href="https://calendly.com/startscreen/demo"
              >
                Book a demo
              </a>
            )}

            {hasLoggedIn ? (
              <div className="ml-0 sm:ml-6">
                <Link
                  href={
                    data && data.user ? `/org/${data.user.orgID}` : '/signin'
                  }
                >
                  <a className="flex w-full items-center justify-center rounded-md active:scale-95 bg-indigo-600 hover:bg-indigo-700 py-2 px-6 font-medium text-white transition-all duration-200 ease-in-out">
                    Dashboard
                  </a>
                </Link>
              </div>
            ) : (
              <div className="flex items-center">
                <Link className="" href="/signin">
                  <a className="mr-4 hidden sm:block transition-all duration-200 ease-in-out rounded-md active:scale-95 hover:bg-[#272A3F] py-2 px-6">
                    Login
                  </a>
                </Link>
                <Link className="" href="/signup">
                  <a className="flex items-center justify-center rounded-md active:scale-95 bg-indigo-600 hover:bg-indigo-700 py-2 px-6 font-medium text-white transition-all duration-200 ease-in-out">
                    Sign up
                  </a>
                </Link>
              </div>
            )}
          </div>
        </div>
        <div
          className="mx-auto max-w-5xl px-5 mt-8 mb-10 sm:mb-16"
          data-aos="fade-up"
        >
          <div className="text-center">
            <div className="grid gap-8 items-start justify-center ">
              <div className="relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-300 to-indigo-600 rounded-lg blur opacity-75 transition duration-1000 animate-tilt"></div>
                <button className="relative cursor-default px-5 py-2 bg-[#0D0E25] rounded-lg leading-none flex items-center divide-x divide-gray-600">
                  {hasLoggedIn ? (
                    <span className="cursor-text flex items-center justify-center">
                      <p className="text-white mr-2 font-medium py-1">
                        {' '}
                        <span className="mr-2 animate-wiggle h-5">👋</span>{' '}
                        Welcome back!
                      </p>
                    </span>
                  ) : (
                    <span className="cursor-text flex items-center justify-center">
                      <img className="h-4 w-4 mr-1" src="/retool-white.svg" />
                      <p className="text-white ml-2 mr-2 text-base font-medium">
                        {' '}
                        Built for Retool
                      </p>
                    </span>
                  )}
                </button>
              </div>
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mt-10 text-white">
              Support your internal users with ease
            </h1>
            <p className="mx-auto mt-3 sm:max-w-xl max-w-md text-base sm:text-xl sm:mt-8 md:max-w-3xl text-[#b4bcd0]">
              Internal tooling teams thrive when they are building exciting new
              tools. But most spend 30% of their time answering the same user
              questions.
            </p>
            <div className="mx-auto mt-5 max-w-md sm:flex sm:justify-center sm:mt-8">
              <div className="rounded-md shadow">
                {hasLoggedIn ? (
                  <a
                    href={
                      data && data.user ? `/org/${data.user.orgID}` : '/signin'
                    }
                    className="active:scale-95 flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-8 py-3 text-white hover:bg-indigo-700 sm:py-4 sm:px-10 sm:text-lg font-medium transition-all duration-200 ease-in-out"
                  >
                    Go to dashboard
                  </a>
                ) : (
                  <a
                    target="_blank"
                    href="https://calendly.com/bengahtan/30min"
                    className="active:scale-95 flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-8 py-3 text-white hover:bg-indigo-700 sm:py-4 sm:px-10 sm:text-lg font-medium transition-all duration-200 ease-in-out"
                  >
                    Book a demo
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="mx-auto w-full mt-14 sm:mt-20 relative">
            <div class="absolute right-0 hidden md:block -top-20">
              <p class="text-[#b4bcd0] font-medium">See how it works</p>
              <svg
                width="40"
                height="100%"
                viewBox="0 0 68 77"
                class="fill-current text-[#b4bcd0]"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M36.4058 21.9334L41.1332 16.3559C39.2314 17.9379 37.6704 19.8143 36.4058 21.9334ZM23.1688 30.9494C25.2167 29.029 27.1793 27.057 29.4146 25.3858C26.7801 26.6313 24.9064 28.7351 23.1688 30.9494ZM33.2576 34.8628C33.7854 35.8059 34.3218 35.3399 34.8298 34.9399C36.5485 33.5884 36.678 31.5876 36.7834 29.6735C36.8603 28.2784 36.7645 26.8185 36.0412 25.5301C35.7657 25.0387 35.412 24.7259 34.9817 25.4387C33.8074 27.3851 32.9252 29.4359 32.76 31.7319C32.6813 32.8257 32.8093 33.9015 33.2576 34.8628ZM30.044 26.4983C28.4758 27.3028 27.2555 28.5674 26.0094 29.7814C20.6252 35.0274 17.2489 41.4539 14.905 48.5197C13.3395 53.2397 12.2395 58.0626 11.3075 62.9339C11.2221 63.3783 11.1535 63.8311 10.8458 64.1895C10.4999 64.592 10.1735 64.7341 10.044 64.0384C10.0116 63.8672 10.1324 63.62 9.81447 63.6931C8.71764 63.9447 8.57833 63.1202 8.38728 62.4074C8.02827 61.0698 8.27652 59.7088 8.39131 58.3751C8.71242 54.6385 9.17526 50.9104 10.1232 47.2754C11.0034 43.9008 12.3627 40.6916 13.8111 37.5224C14.7331 35.505 15.6989 33.4657 17.2506 31.8988C18.2815 30.8578 18.8385 29.5395 19.8363 28.51C21.7493 26.5365 23.6757 24.5912 25.9913 23.0773C27.8973 21.8315 29.9825 21.148 32.2609 21.1387C33.0121 21.1359 33.5561 20.852 33.993 20.285C35.171 18.7574 36.4016 17.263 37.6421 15.7973C39.6599 13.4123 42.0469 11.3584 44.4974 9.40976C48.406 6.30179 52.5732 3.62757 57.3413 2.00582C60.1437 1.05265 62.9687 0.295021 65.9574 1.11102C66.7443 1.32581 67.4328 1.66676 67.8401 2.42598C68.016 2.75297 68.0549 3.05479 67.8179 3.37093C67.5375 3.74424 67.3526 3.49944 67.042 3.35738C64.9118 2.3824 62.9132 3.24742 60.9212 3.90867C55.4318 5.7307 50.8176 9.01166 46.4398 12.6746C43.0898 15.4778 40.0455 18.5808 37.3679 22.0365C37.1552 22.3109 36.7658 22.5228 37.3009 22.9146C39.1654 24.2792 40.116 26.2265 40.5107 28.4278C41.1203 31.8255 40.7845 35.0594 38.2512 37.6832C36.8901 39.0932 35.496 39.1912 33.7364 38.3538C29.2533 36.221 28.3904 31.6356 29.8533 27.7477C29.9848 27.3997 30.1159 27.0518 30.2468 26.7046C30.1792 26.6355 30.1114 26.567 30.044 26.4983Z"
                ></path>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.7396 69.066C11.1773 70.7696 9.87903 72.6455 8.96903 74.7731C10.2258 72.8709 11.4825 70.9684 12.7396 69.066ZM4.16018 63.3217C5.28587 65.375 6.04163 67.5845 6.8814 69.7589C7.00686 70.0839 6.98214 70.5315 7.50699 70.5757C8.01112 70.618 8.14131 70.2023 8.2923 69.9013C9.11672 68.2617 10.3763 66.9558 11.4775 65.5213C12.1728 64.6148 12.7024 63.5696 13.4452 62.6601C14.5892 61.2601 15.6297 59.7727 16.6756 58.2955C17.2058 57.547 18.0634 57.0791 18.326 56.0843C18.5838 55.1085 19.3593 54.91 20.7139 55.5058C21.4054 55.8096 21.9699 56.4212 21.6002 57.3018C21.2961 58.026 20.9306 58.7392 20.4918 59.389C17.5869 63.6895 14.6474 67.9665 11.7376 72.2636C11.0844 73.228 10.4955 74.2376 9.88906 75.2334C8.78653 77.0441 7.96815 77.2015 6.16577 76.0678C4.76664 75.1877 3.99186 73.9692 3.66283 72.3613C3.02909 69.2664 2.30718 66.1894 1.09654 63.2558C0.853796 62.668 0.528696 62.1149 0.262714 61.5357C-0.0458552 60.8654 0.158774 60.5306 0.921301 60.5897C2.61148 60.7196 2.87504 60.94 4.16018 63.3217Z"
                ></path>
              </svg>
            </div>
            <iframe
              alt="StartScreen Demo Video"
              className="mx-auto aspect-[900/507] w-full rounded-md"
              src="https://www.loom.com/embed/911d37373fe44ec7accd34a36b9236a2?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true"
              frameBorder="0"
              webkitallowfullscreen
              mozallowfullscreen
              allowFullScreen
              loading="lazy"
            ></iframe>
          </div>
        </div>

        <div className="px-5 bg-[#000212] text-sm font-medium sm:py-16 py-10">
          <div className="mx-auto max-w-7xl">
            <a
              target="_blank"
              href="https://twitter.com/startscreen_io"
              className="text-[#b4bcd0] hover:text-white transition-all duration-200 ease-in-out"
            >
              Follow @StartScreen for updates
            </a>
            <div className="flex mt-3">
              <a
                href="/privacy"
                target="_blank"
                className="mr-4 text-[#b4bcd0] hover:text-white transition-all duration-200 ease-in-out"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                target="_blank"
                className="mr-4 text-[#b4bcd0] hover:text-white transition-all duration-200 ease-in-out"
              >
                Terms of Service
              </a>
              <a
                href="mailto:team@startscreen.io"
                className="mr-4 text-[#b4bcd0] hover:text-white transition-all duration-200 ease-in-out"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
